import { useId } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { gr } from "../tokens";
import icons from "../icons";
import GrIcon from "./GrIcon";
import GrButton from "./GrButton";

// "Are you sure…?" confirmation. Centred: icon on top (red trash for destructive, teal question
// otherwise), the question in 16/500 (readable, not shouting), then Cancel (quiet) + confirm,
// each half the width. Wording comes from the caller unchanged.
export default function GrConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  text,
  confirmLabel = "Agree",
  cancelLabel = "Cancel",
  destructive = false,
  icon,
}) {
  const titleId = `gr-confirm-${useId()}`;
  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      aria-labelledby={titleId}
      BackdropProps={{ sx: { background: gr.color.overlay } }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 380,
          m: gr.space[4],
          p: gr.space[6],
          borderRadius: gr.radius.card,
          boxShadow: gr.shadow.raised,
          backgroundImage: "none",
          fontFamily: gr.font.family,
          textAlign: "center",
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          mx: "auto",
          mb: gr.space[4],
          width: gr.control.lg,
          height: gr.control.lg,
          borderRadius: gr.radius.pill,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: destructive ? gr.color.dangerSoft : gr.color.brandSoft,
          color: destructive ? gr.color.danger : gr.color.brandDark,
          "& svg": { width: gr.icon.xl, height: gr.icon.xl },
        }}
      >
        <GrIcon icon={icon || (destructive ? icons.trash : icons.alert)} />
      </Box>
      <Box
        component="h2"
        id={titleId}
        sx={{
          m: 0,
          fontSize: gr.font.size.lg,
          fontWeight: gr.font.weight.medium,
          lineHeight: gr.font.lineHeight.normal,
          color: gr.color.ink,
        }}
      >
        {title}
      </Box>
      {text ? (
        <Box
          component="p"
          sx={{
            m: 0,
            mt: gr.space[2],
            fontSize: gr.font.size.sm,
            color: gr.color.muted,
          }}
        >
          {text}
        </Box>
      ) : null}
      <Box
        sx={{
          mt: gr.space[6],
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: gr.space[3],
        }}
      >
        <GrButton variant="quiet" onClick={onClose} fullWidth>
          {cancelLabel}
        </GrButton>
        <GrButton
          variant={destructive ? "danger" : "primary"}
          onClick={onConfirm}
          fullWidth
        >
          {confirmLabel}
        </GrButton>
      </Box>
    </Dialog>
  );
}

GrConfirmDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  title: PropTypes.node.isRequired,
  text: PropTypes.node,
  confirmLabel: PropTypes.node,
  cancelLabel: PropTypes.node,
  destructive: PropTypes.bool,
  // Override the top icon (default: trash when destructive, alert otherwise).
  icon: PropTypes.object,
};
